import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row } from 'react-table';
import { Products } from '@/common/const';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { Organization } from '@/common/types';
import { PatientProductBadges } from '@/components/Patients/ProductBadges/ProductBadges';
import { SubmitCancelButtons } from '@/components/common/Form/Form';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { MultiSelectFilter } from '@/components/common/Table/Filters/MultiSelectFilter';
import { SingleSelectFilter } from '@/components/common/Table/Filters/SingleSelectFilter';
import { Button } from '@/components/common/button/button';
import { Center } from '@/components/common/styles';
import { useModalContext } from '@/components/context/hooks/ModalContext';
import { resultIsError } from '@/services/HttpService';
import { OrganizationsTable } from './OrganizationsTable';

const STATUS_FILTER_OPTIONS = [
    { id: 'enabled', name: 'Enabled' },
    { id: 'disabled', name: 'Disabled' },
];

const PRODUCT_FILTER_OPTIONS = Object.keys(Products).map((key) => ({
    id: key,
    name: Products[key],
}));

export const OrganizationsTableContainer = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [rows, setRows] = useState<Organization[]>();
    const [statusFilter, setStatusFilter] = useState<string | null>('enabled');
    const [productFilters, setProductFilters] = useState<string[]>(['SCAN']);
    const { httpService } = useHttpContext();
    const { setDialog, setDialogOpen } = useModalContext();
    const { infoToast, successToast, errorToast } = toasts;
    const navigate = useNavigate();

    const getOrganizations = async () => {
        setIsLoading(true);
        const res = await httpService.getOrganizations();
        if (resultIsError(res)) {
        } else {
            const orderedByName = res.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
            setRows(orderedByName);
        }
        setIsLoading(false);
    };

    const handleDisable = async (organizationId: number) => {
        setDialogOpen(false);
        infoToast('Attempting to disable organization. This may take a few minutes.');
        setIsLoading(true);
        const res = await httpService.disableOrganizationAdmin(organizationId);
        if (resultIsError(res)) {
            errorToast(`Failed to disable organization`);
        } else {
            successToast(`Successfully disabled organization`);
            await getOrganizations();
        }
    };

    const handleEnable = async (organizationId: number) => {
        setDialogOpen(false);
        infoToast('Attempting to enable organization. This may take a few minutes.');
        setIsLoading(true);
        const res = await httpService.enableOrganizationAdmin(organizationId);
        if (resultIsError(res)) {
            errorToast(`Failed to enable organization`);
        } else {
            successToast(`Successfully enabled organization`);
            await getOrganizations();
        }
    };

    useEffect(() => {
        (async () => {
            await getOrganizations();
        })();
    }, []);

    const handleEnableClick = (organizationId: number) => {
        setDialog(
            'Enable Organization?',
            <Center>
                <p>
                    Are you sure you want to enable this organization? This will restore any previous access to Exer
                    applications for patients and providers.
                </p>
                <SubmitCancelButtons
                    submitText="Enable"
                    submitFunction={() => handleEnable(organizationId)}
                    cancelFunction={() => setDialogOpen(false)}
                />
            </Center>,
        );
        setDialogOpen(true);
    };
    const handleDisableClick = (organizationId: number) => {
        setDialog(
            'Disable Organization?',
            <Center>
                <p>
                    Are you sure you want to disable this organization? This will revoke access to all Exer applications
                    and discharge all patients.
                </p>
                <SubmitCancelButtons
                    submitText="Disable"
                    submitFunction={() => handleDisable(organizationId)}
                    cancelFunction={() => setDialogOpen(false)}
                />
            </Center>,
        );
        setDialogOpen(true);
    };

    const columnStructure = [
        {
            Header: 'Name',
            accessor: 'name',
            Cell: ({ value, row }: { value: string; row: Row }) => {
                const { id } = row.original as Organization;
                return (
                    <Button onClick={() => navigate(`/admin/organizations/${id}/`)} size="small" variant="primary">
                        {value}
                    </Button>
                );
            },
        },
        {
            Header: 'TimeZone',
            accessor: 'timezone',
            Cell: ({ value }) => <strong>{value}</strong>,
        },
        {
            Header: 'Products',
            accessor: 'products',
            Cell: ({ value }) => {
                return <PatientProductBadges products={value} />;
            },
        },
        {
            Header: 'Enable/Disable',
            accessor: 'disabled',
            Cell: ({ value, row }: { value: boolean; row: Row }) => {
                const { id } = row.original as Organization;
                return value ? (
                    <Button onClick={() => handleEnableClick(id)} size="small" variant="primary">
                        Enable
                    </Button>
                ) : (
                    <Button onClick={() => handleDisableClick(id)} size="small" variant="tertiary">
                        Disable
                    </Button>
                );
            },
        },
    ];

    const matchesStatus = (organization: Organization) => {
        if (statusFilter === null) {
            return true;
        }
        return statusFilter === 'disabled' ? organization.disabled : !organization.disabled;
    };

    const matchesProducts = (organization: Organization) => {
        if (productFilters.length === 0) {
            return true;
        }
        return productFilters.some((product) => organization.products?.includes(product));
    };

    const filteredRows = rows?.filter((organization) => matchesStatus(organization) && matchesProducts(organization));

    const handleProductChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setProductFilters((current) => (checked ? [...current, name] : current.filter((product) => product !== name)));
    };

    const filterControls = (
        <>
            <SingleSelectFilter
                options={STATUS_FILTER_OPTIONS}
                selected={statusFilter}
                handleChange={setStatusFilter}
                label="Status"
                prefix="organization-status-"
                menuAlignment="left"
                showSelectedLabel
            />
            <MultiSelectFilter
                options={PRODUCT_FILTER_OPTIONS}
                selected={productFilters}
                handleRadioChange={handleProductChange}
                handleClearSelection={() => setProductFilters([])}
                label="Products"
                prefix="organization-products-"
                menuAlignment="left"
            />
        </>
    );

    return (
        <div>
            {isLoading ? (
                <LoadingSpinner />
            ) : (
                filteredRows && (
                    <OrganizationsTable
                        allowAddOrgs
                        columnStructure={columnStructure}
                        data={filteredRows}
                        filters={filterControls}
                    />
                )
            )}
        </div>
    );
};
